// Resumable upload state machine, generic over what the server stores (a source file by default, or any record keyed by its sha256): hash the file (WebCrypto, up to 128 MiB) and reuse a stored file of the same sha256, otherwise create or resume a session (IndexedDB fingerprint), send the missing parts three at a time with their Content-Digest and automatic retries (0, 1, 3, 5, 10 s for network failures, 408, 429, 5xx and a part damaged in transit; other problems stop at once), report bytes, speed and time left, pause and resume, then complete (the server verifies the sha256 and %PDF- and checks the PDF in its sandbox; a file it rejects, also one the check refused, is forgotten at once) and forget the paused record; the transport, record store and hash are injected so tests run without a network.
import type { SourceFileOut, UploadPart, UploadSession } from "@/api/__generated__";

import { hashFile } from "./hash";
import { contentDigest, fingerprint, missingParts, partRanges, retryDelay, type PartRange } from "./parts";
import type { UploadRecordStore } from "./store";

export type UploadPhase =
  | "idle"
  | "hashing"
  | "checking"
  | "duplicate"
  | "uploading"
  | "paused"
  | "verifying"
  | "stored"
  | "rejected"
  | "error";

export interface StoredFile {
  sha256: string;
}

export type UploadSessionOf<TStored> = Omit<UploadSession, "deduplicated_source"> & {
  deduplicated_source: TStored | null;
};

export interface UploadResultOf<TStored> {
  source_file: TStored;
  deduplicated: boolean;
  profile_job_id: string | null;
}

export type PartSender = (
  uploadId: string,
  partNumber: number,
  data: ArrayBuffer,
  digest: string,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
) => Promise<UploadPart>;

export interface UploadState<TStored extends StoredFile = SourceFileOut> {
  phase: UploadPhase;
  fileName: string | null;
  sizeBytes: number;
  sha256: string | null;
  uploadId: string | null;
  sentBytes: number;
  bytesPerSecond: number | null;
  secondsLeft: number | null;
  sourceFile: TStored | null;
  deduplicated: boolean;
  profileJobId: string | null;
  problem: unknown;
}

export interface UploadTransport<TStored extends StoredFile = SourceFileOut> {
  findSourceFile: (sha256: string) => Promise<TStored | null>;
  createUpload: (body: {
    file_name: string;
    size_bytes: number;
    content_type: string;
    sha256: string | null;
  }) => Promise<UploadSessionOf<TStored>>;
  getUpload: (uploadId: string) => Promise<UploadSessionOf<TStored> | null>;
  putPart: PartSender;
  completeUpload: (uploadId: string, profile: boolean) => Promise<UploadResultOf<TStored>>;
  deleteUpload: (uploadId: string) => Promise<void>;
}

export interface UploaderOptions<TStored extends StoredFile = SourceFileOut> {
  transport: UploadTransport<TStored>;
  records: UploadRecordStore;
  hash?: (file: File, signal: AbortSignal) => Promise<string | null>;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  now?: () => number;
  concurrency?: number;
  profile?: boolean;
}

export const IDLE: UploadState<never> = {
  phase: "idle",
  fileName: null,
  sizeBytes: 0,
  sha256: null,
  uploadId: null,
  sentBytes: 0,
  bytesPerSecond: null,
  secondsLeft: null,
  sourceFile: null,
  deduplicated: false,
  profileJobId: null,
  problem: null,
};

const PDF_CONTENT_TYPE = "application/pdf";
const REJECTING_CODES = new Set(["not-a-pdf", "digest-mismatch", "upload-refused"]);
const SPEED_WINDOW_MS = 1_500;

export function isRetryable(problem: unknown): boolean {
  if (problem instanceof DOMException && problem.name === "AbortError") {
    return false;
  }
  const status = (problem as { status?: unknown } | null)?.status;
  const code = (problem as { code?: unknown } | null)?.code;
  if (code === "network-error" || code === "digest-mismatch" || typeof status !== "number") {
    return true;
  }
  return status === 408 || status === 429 || (status >= 500 && status !== 501);
}

function aborted(signal: AbortSignal): boolean {
  return signal.aborted;
}

function defaultSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (ms <= 0) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(new DOMException("Paused.", "AbortError"));
    });
  });
}

export class Uploader<TStored extends StoredFile = SourceFileOut> {
  private state: UploadState<TStored> = IDLE;
  private readonly listeners = new Set<() => void>();
  private controller: AbortController | null = null;
  private file: File | null = null;
  private readonly options: Required<Omit<UploaderOptions<TStored>, "transport" | "records">> &
    Pick<UploaderOptions<TStored>, "transport" | "records">;
  private inFlight = new Map<number, number>();
  private confirmedBytes = 0;
  private samples: { at: number; bytes: number }[] = [];

  constructor(options: UploaderOptions<TStored>) {
    this.options = {
      hash: (file, signal) => hashFile(file, signal),
      sleep: defaultSleep,
      now: () => Date.now(),
      concurrency: 3,
      profile: true,
      ...options,
    };
  }

  getState = (): UploadState<TStored> => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  async start(file: File): Promise<void> {
    this.abortRun();
    this.file = file;
    this.update({ ...IDLE, phase: "hashing", fileName: file.name, sizeBytes: file.size });
    await this.run();
  }

  async resume(): Promise<void> {
    if (this.file === null || (this.state.phase !== "paused" && this.state.phase !== "error")) {
      return;
    }
    await this.run();
  }

  pause(): void {
    if (
      this.state.phase === "uploading" ||
      this.state.phase === "hashing" ||
      this.state.phase === "checking"
    ) {
      this.abortRun();
      this.update({ phase: "paused", bytesPerSecond: null, secondsLeft: null });
    }
  }

  async cancel(): Promise<void> {
    const { uploadId } = this.state;
    const file = this.file;
    this.abortRun();
    this.file = null;
    this.update(IDLE);
    if (file !== null) {
      await this.options.records.remove(fingerprint(file));
    }
    if (uploadId !== null) {
      await this.options.transport.deleteUpload(uploadId).catch(() => undefined);
    }
  }

  reset(): void {
    this.abortRun();
    this.file = null;
    this.update(IDLE);
  }

  private abortRun(): void {
    this.controller?.abort();
    this.controller = null;
  }

  private update(patch: Partial<UploadState<TStored>>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) {
      listener();
    }
  }

  private async run(): Promise<void> {
    const file = this.file;
    if (file === null) {
      return;
    }
    const controller = new AbortController();
    this.controller = controller;
    const { signal } = controller;
    let completing = false;
    try {
      let sha256 = this.state.sha256;
      if (sha256 === null && this.state.uploadId === null) {
        this.update({ phase: "hashing" });
        sha256 = await this.options.hash(file, signal);
        if (aborted(signal)) {
          return;
        }
        this.update({ sha256 });
        if (sha256 !== null) {
          this.update({ phase: "checking" });
          const existing = await this.options.transport.findSourceFile(sha256);
          if (aborted(signal)) {
            return;
          }
          if (existing !== null) {
            this.update({ phase: "duplicate", sourceFile: existing, deduplicated: true });
            return;
          }
        }
      }
      const session = await this.session(file, sha256);
      if (aborted(signal)) {
        return;
      }
      if (session.state === "completed" && session.deduplicated_source !== null) {
        this.update({ phase: "duplicate", sourceFile: session.deduplicated_source, deduplicated: true });
        return;
      }
      await this.sendParts(file, session, signal);
      if (aborted(signal)) {
        return;
      }
      this.update({ phase: "verifying", bytesPerSecond: null, secondsLeft: 0 });
      completing = true;
      const result = await this.options.transport.completeUpload(session.upload_id, this.options.profile);
      await this.options.records.remove(fingerprint(file));
      this.update({
        phase: "stored",
        sourceFile: result.source_file,
        deduplicated: result.deduplicated,
        profileJobId: result.profile_job_id,
        sha256: result.source_file.sha256,
        sentBytes: file.size,
      });
    } catch (problem) {
      if (aborted(signal)) {
        return;
      }
      const code = (problem as { code?: unknown } | null)?.code;
      if (completing && typeof code === "string" && REJECTING_CODES.has(code)) {
        await this.options.records.remove(fingerprint(file));
        this.update({ phase: "rejected", problem });
        return;
      }
      this.update({ phase: "error", problem, bytesPerSecond: null, secondsLeft: null });
    } finally {
      if (this.controller === controller) {
        this.controller = null;
      }
    }
  }

  private async session(file: File, sha256: string | null): Promise<UploadSessionOf<TStored>> {
    const key = fingerprint(file);
    const { transport, records } = this.options;
    const known = this.state.uploadId ?? (await records.get(key))?.uploadId ?? null;
    if (known !== null) {
      const existing = await transport.getUpload(known);
      if (existing !== null && existing.state === "receiving") {
        this.update({ uploadId: existing.upload_id });
        return existing;
      }
      await records.remove(key);
    }
    this.update({ phase: "uploading" });
    const created = await transport.createUpload({
      file_name: file.name,
      size_bytes: file.size,
      content_type: file.type || PDF_CONTENT_TYPE,
      sha256,
    });
    if (created.state === "receiving") {
      await records.put({
        fingerprint: key,
        uploadId: created.upload_id,
        fileName: file.name,
        sizeBytes: file.size,
        sha256,
        expiresAt: created.expires_at,
        savedAt: new Date(this.options.now()).toISOString(),
      });
    }
    this.update({ uploadId: created.upload_id });
    return created;
  }

  private async sendParts(file: File, session: UploadSessionOf<TStored>, signal: AbortSignal): Promise<void> {
    const ranges = partRanges(file.size, session.part_size);
    const pending = missingParts(ranges, session.received);
    this.confirmedBytes = ranges
      .filter((range) => session.received.includes(range.number))
      .reduce((sum, range) => sum + range.end - range.start, 0);
    this.inFlight = new Map();
    this.samples = [{ at: this.options.now(), bytes: this.confirmedBytes }];
    this.update({ phase: "uploading", sentBytes: this.confirmedBytes, problem: null });
    const queue = [...pending];
    const workers = Array.from({ length: Math.min(this.options.concurrency, queue.length) }, async () => {
      while (queue.length > 0 && !aborted(signal)) {
        const range = queue.shift();
        if (range !== undefined) {
          await this.sendPart(file, session.upload_id, range, signal);
        }
      }
    });
    await Promise.all(workers);
  }

  private async sendPart(file: File, uploadId: string, range: PartRange, signal: AbortSignal): Promise<void> {
    const data = await file.slice(range.start, range.end).arrayBuffer();
    const digest = await contentDigest(data);
    for (let attempt = 0; ; attempt += 1) {
      try {
        await this.options.transport.putPart(
          uploadId,
          range.number,
          data,
          digest,
          (loaded) => {
            this.progress(range.number, loaded);
          },
          signal,
        );
        this.inFlight.delete(range.number);
        this.confirmedBytes += range.end - range.start;
        this.progress(range.number, 0);
        return;
      } catch (problem) {
        this.inFlight.delete(range.number);
        const delay = retryDelay(attempt + 1);
        if (aborted(signal) || !isRetryable(problem) || delay === null) {
          throw problem;
        }
        await this.options.sleep(delay, signal);
      }
    }
  }

  private progress(partNumber: number, loaded: number): void {
    if (loaded > 0) {
      this.inFlight.set(partNumber, loaded);
    }
    const sent = this.confirmedBytes + [...this.inFlight.values()].reduce((sum, value) => sum + value, 0);
    const now = this.options.now();
    this.samples.push({ at: now, bytes: sent });
    while (this.samples.length > 2 && now - (this.samples[0]?.at ?? now) > SPEED_WINDOW_MS) {
      this.samples.shift();
    }
    const first = this.samples[0];
    const elapsed = first === undefined ? 0 : (now - first.at) / 1000;
    const speed = first !== undefined && elapsed > 0 ? (sent - first.bytes) / elapsed : null;
    const remaining = this.state.sizeBytes - sent;
    this.update({
      sentBytes: Math.min(sent, this.state.sizeBytes),
      bytesPerSecond: speed,
      secondsLeft: speed !== null && speed > 0 ? Math.ceil(remaining / speed) : null,
    });
  }
}
